/**
 * NotificationsProvider
 *
 * Sincroniza el `notificationsStore` con los stores vivos:
 *  - enrollmentsStore (pending)
 *  - chatStore (conversations activas)
 *  - agentsStore (agents + updateStates)
 *  - Manifest del canal stable para detectar desactualizados.
 *
 * Usa `getState()` + `subscribe` de zustand directamente (sin hooks React)
 * porque los stores son singletons y pueden cambiar fuera del ciclo de vida
 * del componente (ej. WebSocket manager).
 *
 * Estrategia de suscripción:
 *  - Un único subscribe a cada store con un selector que devuelve lo que
 *    realmente dispara cambios. El provider reconcilia con `bulkSet` por kind.
 *  - latestVersion se fetcha cada 5 minutos (mismo endpoint que UpdatesPage).
 */

import { useEffect, useRef } from 'react';
import { useEnrollmentsStore } from '@/store/enrollmentsStore';
import { useChatStore } from '@/store/chatStore';
import { useAgentsStore } from '@/store/agentsStore';
import { useNotificationsStore } from '@/store/notificationsStore';
import { buildNotifications } from '@/lib/notifications/buildNotifications';
import apiClient from '@/api/client';

interface UpdateManifest {
  version: string;
}

interface NotificationsProviderProps {
  children: React.ReactNode;
}

const LATEST_VERSION_REFRESH_MS = 5 * 60 * 1000;

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  // latestVersion se mantiene en ref para evitar re-renders cuando cambia;
  // la reconciliación sucede por el efecto combinado.
  const latestVersionRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Hidratar read flags desde localStorage al montar.
    useNotificationsStore.getState().hydrateReadFromStorage();

    // Función de reconciliación: lee los stores vivos, construye notifs y
    // sincroniza por kind. Idempotente — se puede llamar en cualquier momento.
    const reconcile = () => {
      const enrollments = useEnrollmentsStore.getState().enrollments;
      const conversations = useChatStore.getState().conversations;
      const agents = useAgentsStore.getState().agents;
      const updateStates = useAgentsStore.getState().updateStates;
      const latestVersion = latestVersionRef.current;

      const built = buildNotifications({
        pendingEnrollments: enrollments,
        conversations,
        agents,
        updateStates,
        latestVersion,
      });

      const store = useNotificationsStore.getState();
      // bulkSet por cada kind presente en `built`. Para los kinds que no
      // aparecen en este snapshot, también queremos reconciliar a vacío para
      // que las notifs que desaparecieron del store vivo se vayan del panel.
      const kinds: Array<'enrollment' | 'chat' | 'update' | 'update-failed'> = [
        'enrollment',
        'chat',
        'update',
        'update-failed',
      ];
      for (const k of kinds) {
        const items = built.filter((n) => n.kind === k);
        store.bulkSet(k, items);
      }
    };

    // Subscriptions: usar zustand vanilla para reaccionar a cambios sin
    // depender del ciclo React. El selector `subscribeWithSelector` requiere
    // middleware; usamos un selector manual que evita renders innecesarios.
    const unsubEnrollments = useEnrollmentsStore.subscribe((state, prev) => {
      if (state.enrollments !== prev.enrollments) reconcile();
    });
    const unsubChat = useChatStore.subscribe((state, prev) => {
      if (state.conversations !== prev.conversations) reconcile();
    });
    const unsubAgents = useAgentsStore.subscribe((state, prev) => {
      if (state.agents !== prev.agents || state.updateStates !== prev.updateStates) {
        reconcile();
      }
    });

    // Fetch inicial + refetch cada 5 minutos del manifest del canal stable.
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const fetchLatestVersion = async () => {
      try {
        const res = await apiClient.get<UpdateManifest>(
          '/agent-update?channel=stable&portable=true'
        );
        if (cancelled) return;
        const v = res.data?.version;
        if (v && v !== latestVersionRef.current) {
          latestVersionRef.current = v;
          reconcile();
        }
      } catch {
        // Sin manifiesto configurado (404) o backend caído. No spamear logs;
        // el panel sigue funcionando con notifications de chat/enrollment/update-failed.
      }
    };

    void fetchLatestVersion();
    timer = setInterval(() => {
      void fetchLatestVersion();
    }, LATEST_VERSION_REFRESH_MS);

    // Reconciliación inicial — corre después de que los stores hidraten
    // desde WS / queries. Usar microtask para no bloquear el mount.
    queueMicrotask(reconcile);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      unsubEnrollments();
      unsubChat();
      unsubAgents();
    };
  }, []);

  return <>{children}</>;
}

export default NotificationsProvider;
